import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { dateLocaleFor } from '@/lib/localeFormat';
import { Star, Send, MessageSquare, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { emailPlaceholder } from '@/hooks/useStorefront';
import { useLanguage } from '@/hooks/useLanguage';
import { Sparkles } from 'lucide-react';

interface Review {
  id: string;
  author_name: string;
  rating: number;
  comment: string | null;
  admin_reply: string | null;
  admin_reply_at: string | null;
  created_at: string;
  verified_purchase?: boolean | null;
}

interface TranslationRow {
  review_id: string;
  comment: string | null;
  auto_translated: boolean;
  admin_reply?: string | null;
}

interface Props {
  productId: string;
  productTitle: string;
}

export default function ReviewSection({ productId, productTitle }: Props) {
  const { t } = useTranslations();
  const { locale } = useLanguage();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [translations, setTranslations] = useState<Record<string, TranslationRow>>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  useEffect(() => {
    if (locale === 'de' || reviews.length === 0) {
      setTranslations({});
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = reviews.filter((r) => r.comment).map((r) => r.id);
      if (ids.length === 0) return;
      const { data } = await supabase
        .from('review_translations')
        .select('review_id, comment, auto_translated, admin_reply')
        .eq('locale', locale)
        .in('review_id', ids);
      if (cancelled) return;
      const map: Record<string, TranslationRow> = {};
      (data ?? []).forEach((row: any) => { map[row.review_id] = row; });
      setTranslations(map);
    })();
    return () => { cancelled = true; };
  }, [reviews, locale]);

  const fetchReviews = async () => {
    const { data } = await supabase
      .from('reviews')
      .select('id, author_name, rating, comment, admin_reply, admin_reply_at, created_at, verified_purchase')
      .eq('product_id', productId)
      .eq('is_approved', true)
      .order('created_at', { ascending: false });
    setReviews((data as Review[]) || []);
    setLoading(false);
  };

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    pct: reviews.length > 0 ? Math.round((reviews.filter(r => r.rating === star).length / reviews.length) * 100) : 0,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || rating === 0) {
      toast.error(t('reviews.fill_required', 'Bitte Name, E-Mail und Bewertung angeben'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error(t('reviews.invalid_email', 'Bitte eine gültige E-Mail-Adresse eingeben'));
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from('reviews').insert({
      product_id: productId,
      author_name: name.trim(),
      email: email.trim(),
      rating,
      comment: comment.trim() || null,
      is_approved: false,
    } as any);
    if (error) {
      toast.error(t('reviews.error_sending', 'Fehler beim Senden'));
    } else {
      toast.success(t('reviews.submitted', 'Bewertung eingereicht! Sie wird nach Prüfung veröffentlicht.'));
      setName('');
      setEmail('');
      setRating(0);
      setComment('');
      setShowForm(false);
    }
    setSubmitting(false);
  };

  if (loading) return null;

  return (
    <div id="reviews-section" className="mt-12 border-t pt-10">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold">
          {reviews.length > 0
            ? `${reviews.length} ${t('reviews.title_count', 'Bewertungen für')} ${productTitle}`
            : `${t('reviews.title_count', 'Bewertungen für')} ${productTitle}`}
        </h2>
        {!showForm && (
          <Button variant="outline" onClick={() => setShowForm(true)}>
            <Star className="mr-1.5 h-4 w-4" />
            {t('reviews.write', 'Bewertung schreiben')}
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 rounded-xl border bg-card p-5 space-y-4">
          <h3 className="font-semibold">{t('reviews.your_review_for', 'Deine Bewertung für')} {productTitle}</h3>
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('reviews.rating', 'Bewertung')} *</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  type="button"
                  onMouseEnter={() => setHoverRating(s)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(s)}
                  className="p-0.5"
                >
                  <Star className={`h-7 w-7 transition-colors ${
                    s <= (hoverRating || rating) ? 'fill-warning text-warning' : 'text-border hover:text-warning/50'
                  }`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('reviews.name', 'Name')} *</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder={t('reviews.name_placeholder', 'Dein Name')} maxLength={100} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('reviews.email', 'E-Mail')} *</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('reviews.email_placeholder', emailPlaceholder())} maxLength={255} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('reviews.comment', 'Kommentar')} ({t('reviews.optional', 'optional')})</label>
            <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder={t('reviews.comment_placeholder', 'Erzähle uns von deiner Erfahrung...')} rows={3} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting}>
              <Send className="mr-1.5 h-4 w-4" />
              {submitting ? t('reviews.sending', 'Senden...') : t('reviews.submit', 'Absenden')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>{t('general.cancel', 'Abbrechen')}</Button>
          </div>
        </form>
      )}

      {reviews.length > 0 && (
        <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-start">
          <div className="flex flex-col items-center rounded-xl border bg-card p-6 sm:min-w-[160px]">
            <span className="font-mono text-4xl font-bold">{avgRating.toFixed(1)}</span>
            <div className="mt-1 flex gap-0.5">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} className={`h-4 w-4 ${s <= Math.round(avgRating) ? 'fill-warning text-warning' : 'text-border'}`} />
              ))}
            </div>
            <span className="mt-1 text-xs text-muted-foreground">{reviews.length} {t('product.reviews', 'Bewertungen')}</span>
          </div>
          <div className="flex-1 space-y-1.5">
            {ratingCounts.map(({ star, count, pct }) => (
              <div key={star} className="flex items-center gap-2 text-sm">
                <span className="w-16 text-right text-muted-foreground">{star} {t('reviews.stars', 'Sterne')}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-warning transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-xs text-muted-foreground">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-5">
        {reviews.map(review => (
          <div key={review.id} className="rounded-xl border bg-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{review.author_name}</span>
                  {review.verified_purchase && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600"
                      title={t('reviews.verified_purchase', 'Verifizierter Kauf')}
                    >
                      <BadgeCheck className="h-3 w-3" />
                      {t('reviews.verified_purchase', 'Verifizierter Kauf')}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(review.created_at).toLocaleDateString(dateLocaleFor(locale), { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div className="mt-1 flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`h-3.5 w-3.5 ${s <= review.rating ? 'fill-warning text-warning' : 'text-border'}`} />
                  ))}
                </div>
              </div>
            </div>
            {review.comment && (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground">
                  {translations[review.id]?.comment ?? review.comment}
                </p>
                {translations[review.id]?.auto_translated && (
                  <span
                    className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary"
                    title={t('reviews.auto_translated_title', 'Automatisch übersetzt')}
                  >
                    <Sparkles className="h-3 w-3" />
                    {t('reviews.auto_translated', 'Automatisch übersetzt')}
                  </span>
                )}
              </div>
            )}
            {review.admin_reply && (
              <div className="mt-4 ml-4 rounded-lg border-l-2 border-primary bg-accent/50 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <MessageSquare className="h-3 w-3" />
                  {t('reviews.reply_from', 'Antwort von BigFollow')}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{translations[review.id]?.admin_reply ?? review.admin_reply}</p>
                {review.admin_reply_at && (
                  <span className="mt-1 block text-[10px] text-muted-foreground">
                    {new Date(review.admin_reply_at).toLocaleDateString(dateLocaleFor(locale), { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
